import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared terminal states for routes.
 *
 * Before these, two distinct situations both rendered an ~80-character blank
 * page: an unmatched URL (no catch-all route existed) and a correct 403 from a
 * fail-closed admin endpoint. From the operator's side both look like the
 * product is broken, so a working permission boundary read as a bug (D4) and a
 * typo read as an outage.
 *
 * The 403 case is worth being explicit about: the server behaving correctly
 * should never look like a crash, or real crashes get dismissed as "just the
 * permissions thing again".
 */

function Shell({ code, title, children, testId }) {
  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
      data-testid={testId}
    >
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center">
        <p className="text-5xl font-bold text-slate-300 mb-3">{code}</p>
        <h1 className="text-xl font-semibold text-slate-900 mb-2">{title}</h1>
        <div className="text-slate-600 text-sm mb-6">{children}</div>
        <Link
          to="/dashboard"
          className="inline-block px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <Shell code="404" title="Page not found" testId="route-not-found">
      That address doesn’t match anything in your workspace. It may have moved,
      or the module may not be enabled for your organization.
    </Shell>
  );
}

export function AccessDeniedPage({ detail }) {
  return (
    <Shell code="403" title="You don’t have access to this" testId="route-access-denied">
      {detail ||
        'This area is restricted to administrators. Ask an owner to grant your department access if you need it.'}
    </Shell>
  );
}
